import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader2 } from 'lucide-react';

import type { SongReferenceStatus } from './useSongReference';

/**
 * 레퍼런스가 준비됐을 때만 노래 진단의 가이드 카드를 연다.
 *
 * 음원을 못 받았을 때 **진단 전체를 세우지 않는다**: 나머지 두 진단은 이미 잰 뒤이고,
 * 노래는 그 위에 얹는 축이다. 그래서 실패 화면은 재시도와 **이 진단만 건너뛰기** 둘을 낸다.
 */

interface SongReferenceGateProps {
  status: SongReferenceStatus;
  onRetry: () => void;
  onSkip: () => void;
  /** 준비됐을 때 그릴 것 — 가이드 카드. */
  children: ReactNode;
}

export const SongReferenceGate = ({
  status,
  onRetry,
  onSkip,
  children,
}: SongReferenceGateProps) => {
  const { t } = useTranslation();

  if (status === 'ready') {
    return children;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <p className="text-center text-sm text-muted-foreground">
          {t('web.diagnosis.song.loadFailed')}
        </p>
        <div className="flex w-full max-w-xs flex-col gap-2">
          <button
            type="button"
            onClick={onRetry}
            // 재시도는 녹음을 시작하지 않는다 — 음원을 다시 받는다. 전진 CTA 인 검은 pill.
            className="flex h-[54px] w-full items-center justify-center rounded-full bg-black font-bold text-white"
          >
            {t('web.diagnosis.song.retry')}
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="min-h-[44px] w-full rounded-xl py-3 font-medium text-muted-foreground"
          >
            {t('web.diagnosis.song.skip')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="flex flex-1 flex-col items-center justify-center gap-3"
    >
      <Loader2
        className="h-6 w-6 text-primary motion-safe:animate-spin"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">
        {t('web.diagnosis.song.loading')}
      </p>
    </div>
  );
};
